import React, { useState } from 'react';
import './AllSBProductionMaterialsDailyReport.css';
import { isOff } from '../utils/inventoryConsumptionReport';

// Dashboard → Small Bags calendar → day popup → Materials report.
// Two checks per operation (server: sb_materials_report.js, docs: report-dashboard.md):
//   stock check — snapshot before − used must equal the snapshot after the operation was closed;
//   plan check  — seeds used vs the norm (off by more than 5% either way).

const MATERIALS = [
  { type: 'Seeds', key: 'seeds', decimals: 2 },
  { type: 'Small Bags', key: 'smallbags', decimals: 0 },
];
const fmt = (v, decimals) => (v === 'No Data' || v == null ? 'No Data' : Number(v).toFixed(decimals));
const signed = (n) => (n > 0 ? '+' : '') + n.toFixed(2);

export function stockCheck(material = {}, decimals) {
  const now = material.qtyNow;
  if (now === 'No Data' || now == null) return 'No Data';
  return Number(now).toFixed(decimals) === Number(material.shouldBe ?? 0).toFixed(decimals) ? 'Yes' : 'No';
}

// Closed operations only: an open one has not returned its seeds yet.
export function planCheck(data) {
  const s = data?.seeds;
  if (!s || !data.closeDate || s.needed == null) return null;
  const needed = Number(s.needed);
  const used = Number(s.qtyUsed);
  return { needed, used, diff: used - needed, off: isOff(needed, used) };
}

// Number on the report icon: failed stock checks plus operations off the plan.
export function countReportIssues(results) {
  return (results || []).reduce((n, p) => {
    if (!p.data || p.error) return n;
    const stock = MATERIALS.filter((m) => stockCheck(p.data[m.key], m.decimals) === 'No').length;
    return n + stock + (planCheck(p.data)?.off ? 1 : 0);
  }, 0);
}

const dateOnly = (v) => (v ? new Date(v).toLocaleDateString() : 'N/A');
const weight = (v) => (v == null ? 'N/A' : Number(v).toFixed(2));

function AllSBProductionMaterialsDailyReport({ isOpen, onClose, date, allData }) {
  const [expanded, setExpanded] = useState({});
  if (!isOpen) return null;
  const products = allData || [];

  return (
    <div className="all-sb-production-materials-daily-report-overlay">
      <div className="all-sb-production-materials-daily-report">
        <div className="all-sb-production-materials-daily-report-header">
          <h3>Materials Report: {date?.toLocaleDateString()}</h3>
          <button className="all-sb-production-materials-daily-report-close-button" onClick={onClose}>×</button>
        </div>
        <div className="all-sb-production-materials-daily-report-content">
          <p className="report-legend">
            <strong>Stock check:</strong> stock before the operation minus what was used must equal the stock
            after it was closed. <strong>Plan check:</strong> seeds used against the norm of the packet.
            A large shortfall usually means bags were not added to the operation.
          </p>
          {products.length === 0 && <p>No data available.</p>}
          {products.map((product, pi) => {
            const data = product.data;
            const plan = data && planCheck(data);
            return (
              <div key={pi} className="product-section">
                <h4>
                  {data?.recID ? `#${data.recID} · ` : ''}{product.sku} · {product.name}
                </h4>
                {product.error ? (
                  <p className="error-text">Error loading report: {product.error}</p>
                ) : (
                  <>
                    <div className="report-meta">
                      {data.closeDate
                        ? <>Closed {data.closeDate}. Stock after: snapshot of {data.nowDate}.</>
                        : <>Operation is not closed yet: stock after and plan check appear once it is.</>}
                    </div>
                    {plan && (
                      <div className={`plan-check${plan.off ? ' plan-check--off' : ''}`}>
                        Seeds needed {plan.needed.toFixed(2)} lb, used {plan.used.toFixed(2)} lb, difference{' '}
                        <strong>{signed(plan.diff)} lb</strong>
                        {plan.off && (plan.diff < 0
                          ? '. Less than the norm: check that every bag was added to the operation.'
                          : '. More than the norm.')}
                      </div>
                    )}
                    <table className="all-sb-production-materials-daily-report-table">
                      <thead>
                        <tr>
                          <th>Material</th><th>Qty Before</th><th>Qty Used</th><th>Qty Now</th><th>Should Be</th><th>Stock check</th>
                        </tr>
                      </thead>
                      <tbody>
                        {MATERIALS.map((m) => {
                          const mat = data[m.key] || {};
                          const check = stockCheck(mat, m.decimals);
                          const key = `${pi}-${m.key}`;
                          const hasBags = m.key === 'seeds' && (mat.bags?.length > 0 || mat.unusedBags?.length > 0);
                          return (
                            <React.Fragment key={m.key}>
                              <tr className={check === 'No' ? 'error-row' : ''}>
                                <td>
                                  {hasBags && (
                                    <button className="expand-button" onClick={() => setExpanded((p) => ({ ...p, [key]: !p[key] }))}>
                                      {expanded[key] ? '−' : '+'}
                                    </button>
                                  )}
                                  {m.type}
                                </td>
                                <td>{fmt(mat.qtyBefore, m.decimals)}</td>
                                <td>{fmt(mat.qtyUsed, m.decimals)}</td>
                                <td>{fmt(mat.qtyNow, m.decimals)}</td>
                                <td>{fmt(mat.shouldBe, m.decimals)}</td>
                                <td className={check === 'No' ? 'error-text' : ''}>{check}</td>
                              </tr>
                              {expanded[key] && hasBags && (
                                <tr>
                                  <td colSpan={6}>
                                    {mat.bags?.length > 0 && (
                                      <>
                                        <h5 className="bags-header">Bags added to the operation</h5>
                                        <table className="bags-subtable">
                                          <thead>
                                            <tr>
                                              <th>Bag</th><th>Lot #</th><th>Weight Before</th><th>Taken</th><th>Returned</th>
                                              <th>Weight After</th><th>Germination</th><th>Received</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {mat.bags.map((bag, bi) => (
                                              <tr key={bi}>
                                                <td>{bag.bagName || 'N/A'}</td>
                                                <td>{bag.lot || 'N/A'}</td>
                                                <td>{weight(bag.weightBefore)}</td>
                                                <td>{weight(bag.takenWeight)}</td>
                                                <td>{weight(bag.returnedWeight)}</td>
                                                <td>{weight(bag.weightAfter)}</td>
                                                <td>{bag.germRate ? `${Number(bag.germRate).toFixed(2)}%` : 'N/A'}</td>
                                                <td>{dateOnly(bag.receivedDate)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </>
                                    )}
                                    {mat.unusedBags?.length > 0 && (
                                      <>
                                        <h5 className="unused-bags-header">Other bags of this seed still in stock</h5>
                                        <table className="bags-subtable">
                                          <thead>
                                            <tr><th>Bag</th><th>Lot #</th><th>Current Weight</th><th>Germination</th><th>Received</th></tr>
                                          </thead>
                                          <tbody>
                                            {mat.unusedBags.map((bag, bi) => (
                                              <tr key={bi}>
                                                <td>{bag.bagName || 'N/A'}</td>
                                                <td>{bag.lot || 'N/A'}</td>
                                                <td>{weight(bag.weightAfter)}</td>
                                                <td>{bag.germRate ? `${Number(bag.germRate).toFixed(2)}%` : 'N/A'}</td>
                                                <td>{dateOnly(bag.receivedDate)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default AllSBProductionMaterialsDailyReport;
